import { useEffect, useRef } from "react";
import type { RefObject } from "react";
import type { CellAssignment, GridPreset } from "../types";
import { verifyDevice } from "../api/devices";
import { useDevicesStore } from "../store/devicesStore";
import { useGridStore } from "../store/gridStore";
import { useGuardiaStore } from "../store/guardiaStore";

// Ventana de inactividad tras un gesto antes de reanudar la cuenta regresiva.
const IDLE_RESUME_MS = 4000;

interface FlatCam {
  deviceId: number;
  channel: number;
}

interface GridSnapshot {
  preset: GridPreset;
  cells: CellAssignment[];
}

// Lista plana global de camaras: todos los equipos habilitados ordenados por
// nombre (orden natural, igual que el DeviceTree), y dentro de cada equipo los
// canales por numero. Los equipos offline quedan incluidos: se verifican al
// armar cada pagina, no se filtran aca.
function buildFlatList(): FlatCam[] {
  const { devices, channelsByDevice } = useDevicesStore.getState();
  const sorted = devices
    .filter((d) => d.is_enabled !== false)
    .slice()
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }),
    );
  const flat: FlatCam[] = [];
  for (const d of sorted) {
    const chans = (channelsByDevice[d.id] ?? [])
      .filter((c) => c.is_enabled !== false)
      .slice()
      .sort((a, b) => a.channel_number - b.channel_number);
    for (const ch of chans) {
      flat.push({ deviceId: d.id, channel: ch.channel_number });
    }
  }
  return flat;
}

// Vuelca una pagina a la grilla. `page` puede tener menos de gridSize camaras
// (ultima pagina parcial); las celdas sobrantes se vacian.
function applyGuardPage(page: FlatCam[]): void {
  const gridSize = useGuardiaStore.getState().gridSize;
  const entries: Array<{ deviceId: number; channel: number } | null> = [];
  for (let i = 0; i < gridSize; i++) {
    const cam = page[i];
    entries.push(cam ? { deviceId: cam.deviceId, channel: cam.channel } : null);
  }
  useGridStore.getState().setGuardPage(entries);
}

export function useGuardiaVirtual(gridContainerRef: RefObject<HTMLDivElement>): {
  start: (intervalSec: number, gridSize: GridPreset) => void;
  stop: () => void;
} {
  const flatListRef = useRef<FlatCam[]>([]);
  const cursorRef = useRef(0); // indice en flatList donde arranca la proxima pagina
  const cycleRef = useRef(0);
  const remainingRef = useRef(0);
  const lockRef = useRef(false); // un advancePage en curso
  const runningRef = useRef(false);
  const lastActivityRef = useRef(Number.NEGATIVE_INFINITY);
  const mouseDownRef = useRef(false);
  const snapshotRef = useRef<GridSnapshot | null>(null);
  const intervalIdRef = useRef<number | null>(null);

  const running = useGuardiaStore((s) => s.running);
  const devices = useDevicesStore((s) => s.devices);
  const channelsByDevice = useDevicesStore((s) => s.channelsByDevice);

  // Pausa "de usuario": maximizar o un gesto reciente. NO incluye lockRef
  // (eso congela la cuenta aparte, sin mostrar "Pausado" en el badge).
  function isUserPaused(): boolean {
    if (useGridStore.getState().maximizedCellId != null) return true;
    if (mouseDownRef.current) return true;
    if (performance.now() - lastActivityRef.current < IDLE_RESUME_MS) return true;
    return false;
  }

  async function advancePage(): Promise<void> {
    if (lockRef.current) return;
    lockRef.current = true;
    try {
      const flat = flatListRef.current;
      const gridSize = useGuardiaStore.getState().gridSize;
      if (flat.length === 0) {
        applyGuardPage([]);
        cycleRef.current = 0;
        useGuardiaStore.getState().patch({ emptyReason: "no-cameras", cycleIndex: 0, totalCycles: 0 });
        return;
      }

      // La pagina NO da la vuelta dentro de si misma: una pasada son chunks
      // secuenciales del flatList; al llegar al final, la pagina termina
      // (parcial) y el cursor vuelve a 0.
      let start = cursorRef.current;
      if (start < 0 || start >= flat.length) start = 0;

      // Walk de planificacion: recorre la franja juntando los deviceId caidos
      // hasta encontrar gridSize camaras online (o llegar al final).
      const devs1 = new Map(useDevicesStore.getState().devices.map((d) => [d.id, d] as const));
      const offlineToCheck = new Set<number>();
      {
        let idx = start;
        let onlineFound = 0;
        while (onlineFound < gridSize && idx < flat.length) {
          const dev = devs1.get(flat[idx].deviceId);
          if (dev?.is_online === true) onlineFound++;
          else if (dev) offlineToCheck.add(flat[idx].deviceId);
          idx++;
        }
      }

      // Requisito 6: intentar reconectar los equipos caidos antes de mostrarlos.
      const recovered = new Set<number>();
      if (offlineToCheck.size > 0) {
        useGuardiaStore.getState().patch({ verifying: true });
        const ids = [...offlineToCheck];
        const results = await Promise.allSettled(ids.map((id) => verifyDevice(id)));
        results.forEach((r, i) => {
          if (r.status === "fulfilled" && r.value?.ok === true) recovered.add(ids[i]);
        });
      }
      if (!runningRef.current) return; // la Guardia se apago durante el await

      // Walk final: arma la pagina con equipos online o recuperados; salta el
      // resto y sigue para mantener la grilla llena.
      const devs2 = new Map(useDevicesStore.getState().devices.map((d) => [d.id, d] as const));
      const page: FlatCam[] = [];
      let idx = start;
      while (page.length < gridSize && idx < flat.length) {
        const cam = flat[idx];
        const dev = devs2.get(cam.deviceId);
        if (dev?.is_online === true || recovered.has(cam.deviceId)) page.push(cam);
        idx++;
      }
      cursorRef.current = idx >= flat.length ? 0 : idx;

      applyGuardPage(page);

      const totalCycles = Math.max(1, Math.ceil(flat.length / gridSize));
      cycleRef.current = start === 0 ? 1 : Math.min(cycleRef.current + 1, totalCycles);
      useGuardiaStore.getState().patch({
        cycleIndex: cycleRef.current,
        totalCycles,
        emptyReason: page.length === 0 ? "all-offline" : "none",
      });
    } finally {
      lockRef.current = false;
      useGuardiaStore.getState().patch({ verifying: false });
    }
  }

  function tick(): void {
    const guardia = useGuardiaStore.getState();
    const userPaused = isUserPaused();
    if (userPaused !== guardia.paused) guardia.patch({ paused: userPaused });
    // La cuenta se congela por gesto/maximizado o mientras se arma una pagina.
    if (userPaused || lockRef.current) return;
    remainingRef.current -= 1;
    if (remainingRef.current <= 0) {
      remainingRef.current = guardia.intervalSec;
      guardia.patch({ remainingSec: remainingRef.current });
      void advancePage();
    } else {
      guardia.patch({ remainingSec: remainingRef.current });
    }
  }

  function start(intervalSec: number, gridSize: GridPreset): void {
    const grid = useGridStore.getState();
    if (grid.maximizedCellId != null) grid.restore();
    snapshotRef.current = {
      preset: useGridStore.getState().preset,
      cells: structuredClone(useGridStore.getState().cells),
    };
    useGridStore.getState().setPreset(gridSize);
    useGridStore.getState().setGuardPage([]); // limpiar: evita flash de camaras previas
    flatListRef.current = buildFlatList();
    const flatLen = flatListRef.current.length;
    cursorRef.current = 0;
    cycleRef.current = 0;
    remainingRef.current = intervalSec;
    lastActivityRef.current = Number.NEGATIVE_INFINITY;
    mouseDownRef.current = false;
    lockRef.current = false;
    runningRef.current = true;
    useGuardiaStore.getState().patch({
      running: true,
      intervalSec,
      gridSize,
      cycleIndex: flatLen === 0 ? 0 : 1,
      totalCycles: flatLen === 0 ? 0 : Math.max(1, Math.ceil(flatLen / gridSize)),
      remainingSec: intervalSec,
      paused: false,
      emptyReason: "none",
    });
    void advancePage();
    if (intervalIdRef.current != null) clearInterval(intervalIdRef.current);
    intervalIdRef.current = window.setInterval(tick, 1000);
  }

  function stop(): void {
    runningRef.current = false;
    if (intervalIdRef.current != null) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    lockRef.current = false;
    mouseDownRef.current = false;
    const snap = snapshotRef.current;
    snapshotRef.current = null;
    if (snap) useGridStore.getState().restoreSnapshot(snap);
    useGuardiaStore.getState().reset();
  }

  // Reconstruye la lista plana cuando cambian devices/channels, re-anclando el
  // cursor a la camara que iniciaba la proxima pagina (evita resetear a la 1).
  useEffect(() => {
    if (!runningRef.current || lockRef.current) return;
    const prev = flatListRef.current;
    const head = prev[cursorRef.current] ?? null;
    const next = buildFlatList();
    flatListRef.current = next;
    if (head) {
      const i = next.findIndex(
        (c) => c.deviceId === head.deviceId && c.channel === head.channel,
      );
      cursorRef.current = i >= 0 ? i : 0;
    } else {
      cursorRef.current = 0;
    }
  }, [devices, channelsByDevice]);

  // Listeners de gestos sobre la grilla (solo mientras corre la Guardia).
  // Eventos de mouse en fase de CAPTURA: el contenedor recibe el evento antes
  // que las celdas, asi un stopPropagation del player de video no nos deja sin
  // registrar el gesto. Pasivos: no hacen preventDefault, el zoom/pan/maximizar
  // siguen intactos.
  useEffect(() => {
    if (!running) return;
    const el = gridContainerRef.current;
    if (!el) return;
    const mark = () => {
      lastActivityRef.current = performance.now();
    };
    const onMouseDown = () => {
      mouseDownRef.current = true;
      mark();
    };
    const onMouseUp = () => {
      mouseDownRef.current = false;
      mark();
    };
    const onMouseMove = (e: MouseEvent) => {
      if (e.buttons !== 0) mark(); // solo arrastre; el hover puro no cuenta
    };
    const add = { capture: true, passive: true } as const;
    const rem = { capture: true } as const;
    el.addEventListener("mousedown", onMouseDown, add);
    el.addEventListener("mousemove", onMouseMove, add);
    el.addEventListener("wheel", mark, add);
    el.addEventListener("dblclick", mark, add);
    window.addEventListener("mouseup", onMouseUp, add);
    return () => {
      el.removeEventListener("mousedown", onMouseDown, rem);
      el.removeEventListener("mousemove", onMouseMove, rem);
      el.removeEventListener("wheel", mark, rem);
      el.removeEventListener("dblclick", mark, rem);
      window.removeEventListener("mouseup", onMouseUp, rem);
    };
  }, [running, gridContainerRef]);

  // Al desmontar (cambio de ruta): detener todo y restaurar la grilla previa.
  useEffect(() => {
    return () => {
      runningRef.current = false;
      if (intervalIdRef.current != null) clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
      const snap = snapshotRef.current;
      snapshotRef.current = null;
      if (snap) useGridStore.getState().restoreSnapshot(snap);
      useGuardiaStore.getState().reset();
    };
  }, []);

  return { start, stop };
}
